import type { CreativePreview } from '../models/credit.types'

/**
 * Legacy `CreativeCostPreview.vue` (`af27283f`): which explanation the estimate shows, checked in
 * this order so the most specific shortfall wins.
 */
export type CreativePreviewKind =
  /** No creative matches the scope — nothing to label or analyse. */
  | 'emptyScope'
  /** Everything is already labelled, but the analysis itself is unaffordable. */
  | 'analysisShortfall'
  /** Neither labelling nor the analysis can run. */
  | 'nothingRuns'
  /** The analysis runs on previously labelled creatives; no new creative is affordable. */
  | 'analysisWithoutNewLabels'
  /** Only part of the new creative scope is affordable. */
  | 'partialLabels'
  | 'affordable'

export interface CreativePreviewSummary {
  kind: CreativePreviewKind
  total: number
  chargeable: number
  alreadyLabeled: number
  /** Labelling cost of the whole chargeable scope. */
  cost: number
  analysisCost: number
  totalRequired: number
  remaining: number
  shortfall: number
  /** New creatives the balance covers (the server's greedy subset). */
  affordableCount: number
}

export function summarizeCreativePreview(
  preview: CreativePreview,
  remaining: number,
): CreativePreviewSummary | null {
  const config = preview.config
  if (!config) return null

  const analysisCost = preview.analysis?.cost ?? 0
  const analysisAffordable = preview.analysis?.affordable ?? true
  const affordableCount = preview.affordable_subset.images + preview.affordable_subset.videos
  const totalRequired = preview.cost + analysisCost
  const summary: Omit<CreativePreviewSummary, 'kind'> = {
    total: config.total,
    chargeable: config.chargeable,
    alreadyLabeled: config.already_labeled,
    cost: preview.cost,
    analysisCost,
    totalRequired,
    remaining,
    shortfall: Math.max(0, totalRequired - remaining),
    affordableCount,
  }

  let kind: CreativePreviewKind = 'affordable'
  if (config.total === 0) kind = 'emptyScope'
  else if (config.chargeable === 0 && !preview.affordable && !analysisAffordable)
    kind = 'analysisShortfall'
  else if (!preview.affordable && !analysisAffordable) kind = 'nothingRuns'
  else if (!preview.affordable && affordableCount === 0) kind = 'analysisWithoutNewLabels'
  else if (!preview.affordable) kind = 'partialLabels'

  return { kind, ...summary }
}
